import React, { useState, useEffect } from 'react';
import { 
  FileText, Info, Cookie, Clock, Layers, Settings, 
  CheckCircle, RefreshCw, Mail, Printer, ArrowUp, 
  MessageCircle, ShieldCheck, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiesPolicy = () => {
  const [activeSection, setActiveSection] = useState('what-cookies-are');
  const navigate = useNavigate();

  // ScrollSpy logic to highlight active section in sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'what-cookies-are';
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          currentSection = section.getAttribute('id');
        }
      });
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Google Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .font-serif {
          font-family: 'Playfair Display', serif;
        }
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        /* Custom Scrollbar for the Sidebar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 4px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: #cbd5e1;
        }

        /* Hide elements during print */
        @media print {
          .no-print { display: none !important; }
          .print-full-width { width: 100% !important; max-width: 100% !important; }
          body { background-color: white !important; }
          .shadow-sm, .shadow-md, .shadow-xl { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
        }
      `}</style>

      <main className="pt-[120px] pb-20 bg-[#f8fafc] min-h-screen font-inter">
        <div className="w-full px-4 lg:px-8 flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          
          {/* Left Sidebar (Sticky & Scrollable) */}
          <aside className="w-[260px] shrink-0 hidden lg:flex flex-col sticky top-[120px] left-0 max-h-[calc(100vh-140px)] overflow-y-auto pb-8 no-print custom-scrollbar">
            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] mb-5 pl-4">
              Contents
            </span>
            <nav className="flex flex-col space-y-1">
              {[
                { id: 'what-cookies-are', label: 'What Cookies Are' },
                { id: 'how-we-use-cookies', label: 'How SENVRA AI Uses Cookies' },
                { id: 'third-party-cookies', label: 'Third-Party Cookies' },
                { id: 'managing-cookies', label: 'Managing Cookies' },
                { id: 'consent', label: 'Consent' },
                { id: 'updates', label: 'Updates' },
                { id: 'contact', label: 'Contact' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-4 py-2.5 text-[14px] rounded-xl transition-all ${
                    activeSection === item.id 
                      ? 'text-[#0284c7] font-bold bg-[#f0f9ff]' 
                      : 'text-slate-500 font-medium hover:text-[#0f172a] hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <button 
              onClick={handlePrint}
              className="mt-8 w-[calc(100%-16px)] ml-2 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Printer size={16} /> Print Document
            </button>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 max-w-[1000px] mx-auto bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden print-full-width">
            
            {/* Header / Hero Area inside the card */}
            <div className="p-8 md:p-12 border-b border-slate-100 relative">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                <div className="w-16 h-16 bg-[#0284c7] rounded-[18px] flex items-center justify-center shadow-lg shadow-sky-500/20 shrink-0">
                  <FileText size={32} className="text-white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-[36px] md:text-[42px] font-serif text-[#0f172a] font-normal leading-tight mb-3 tracking-tight">
                    Cookies Policy
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-[13px]">
                    <span className="bg-[#e0f2fe] text-[#0284c7] px-3 py-1 rounded-md font-bold uppercase tracking-wider text-[10px]">
                      Official Policy
                    </span>
                    <span className="text-slate-400 font-medium">
                      Updated: February 27, 2026
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Info Box */}
              <div className="bg-[#f0f9ff] border border-[#e0f2fe] rounded-2xl p-5 flex items-start gap-4">
                <Info className="text-[#0284c7] shrink-0 mt-0.5" size={20} />
                <p className="text-[#334155] text-[15px] font-medium leading-relaxed">
                  This page explains your rights, payments, and responsibilities clearly to ensure transparency and trust. We believe in transparent policies and fair usage.
                </p>
              </div>
            </div>

            {/* Content Sections */}
            <div className="p-8 md:p-12 space-y-12 md:space-y-16">
              
              {/* What Cookies Are */}
              <section id="what-cookies-are" className="scroll-mt-32">
                <SectionHeader icon={<Cookie size={18} />} title="What Cookies Are" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Cookies are small text files that are stored on your device when you visit a website. They help improve your browsing experience by <strong className="text-slate-800">remembering your preferences</strong> and analyzing how you use the site.
                </p>
              </section>

              {/* How Data Is Used */}
              <section id="how-we-use-cookies" className="scroll-mt-32">
                <SectionHeader icon={<Clock size={18} />} title="How SENVRA AI Uses Cookies" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  We use cookies for the following purposes:
                </p>
                <div className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2">
                  <p><strong className="text-slate-800">Remember user preferences</strong> such as language and login status</p>
                  <p><strong className="text-slate-800">Analyze traffic</strong> to understand which parts of our site are most popular</p>
                  <p><strong className="text-slate-800">Improve performance</strong> by identifying and fixing technical issues</p>
                  <p><strong className="text-slate-800">Measure marketing effectiveness</strong> to provide relevant content and offers</p>
                </div>
              </section>

              {/* Third-Party Cookies */}
              <section id="third-party-cookies" className="scroll-mt-32">
                <SectionHeader icon={<Layers size={18} />} title="Third-Party Cookies" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  In addition to our own cookies, we may also use <strong className="text-slate-800">third-party cookies</strong> from trusted partners like Google Analytics and payment providers to help us analyze usage and process transactions.
                </p>
              </section>

              {/* Managing Cookies */}
              <section id="managing-cookies" className="scroll-mt-32">
                <SectionHeader icon={<Settings size={18} />} title="Managing Cookies" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  You have full control over your cookie settings. Most browsers allow you to <strong className="text-slate-800">disable or delete cookies</strong> through their settings menu. Please note that disabling cookies may affect the functionality of some parts of our website.
                </p>
              </section>

              {/* Consent */}
              <section id="consent" className="scroll-mt-32">
                <SectionHeader icon={<CheckCircle size={18} />} title="Consent" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  By continuing to use our website, you <strong className="text-slate-800">consent to our use of cookies</strong> as described in this policy. We will always notify you of any major changes to our cookie practices.
                </p>
              </section>

              {/* Updates */}
              <section id="updates" className="scroll-mt-32">
                <SectionHeader icon={<RefreshCw size={18} />} title="Updates" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  This policy may be updated from time to time to reflect changes in technology or legal requirements. We encourage you to review this page periodically for the latest information.
                </p>
              </section>

              {/* Contact */}
              <section id="contact" className="scroll-mt-32">
                <SectionHeader icon={<Mail size={18} />} title="Contact" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-5">
                  If you have any questions about our use of cookies, please reach out to us:
                </p>
                <div className="space-y-3">
                  <a href="mailto:contact@senvra.ai" className="flex items-center gap-3 text-[#0284c7] font-medium text-[15px] hover:underline">
                    <Mail size={18} /> contact@senvra.ai
                  </a>
                </div>
              </section>

              {/* Bottom CTA Card */}
              <div className="mt-16 bg-[#f8fafc] border border-slate-200 rounded-[24px] p-10 text-center relative overflow-hidden">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-slate-200 shadow-sm mx-auto mb-5 text-[#0284c7]">
                  <MessageCircle size={22} />
                </div>
                <h3 className="text-[26px] md:text-[28px] font-serif text-[#0f172a] font-bold mb-3 tracking-tight">
                  Still have questions?
                </h3>
                <p className="text-slate-500 text-[15px] mb-8 font-medium">
                  Our team is happy to help clarify anything regarding our policies or services.
                </p>
                <button 
                  onClick={() => navigate('/contact')}
                  className="bg-[#0284c7] hover:bg-[#0369a1] text-white px-8 py-3.5 rounded-xl font-bold text-[14.5px] transition-all flex items-center justify-center gap-2 mx-auto shadow-md"
                >
                  Contact Support <ExternalLink size={16} />
                </button>

                {/* Footer of the CTA Card */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                  <div className="flex gap-4">
                    <span>Terms</span>
                    <span>•</span>
                    <span>Privacy</span>
                    <span>•</span>
                    <span>Cookies</span>
                  </div>
                  <div className="flex items-center gap-3 normal-case tracking-normal font-medium text-slate-500 text-[13px]">
                    <a href="mailto:contact@senvra.ai" className="flex items-center gap-1.5 hover:text-[#0284c7] transition-colors">
                      <Mail size={14} /> contact@senvra.ai
                    </a>
                  </div>
                </div>
                <div className="mt-6 flex flex-col md:flex-row justify-center md:justify-between items-center gap-4">
                   <div className="flex items-center gap-2 normal-case tracking-normal font-medium text-slate-500 text-[13px] mx-auto md:mx-0">
                    <ShieldCheck size={16} className="text-emerald-500" /> We believe in transparent policies and fair usage.
                  </div>
                  <button 
                    onClick={scrollToTop}
                    className="flex items-center gap-1 text-[#0284c7] hover:text-[#0369a1] transition-colors font-bold text-[12px] uppercase tracking-wider"
                  >
                    Back to Top <ArrowUp size={14} />
                  </button>
                </div>
              </div>

            </div>
          </div>
          
        </div>

        {/* Floating Back to top for mobile */}
        <button 
          onClick={scrollToTop}
          className="lg:hidden fixed bottom-6 right-6 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#0284c7] hover:border-[#0284c7] transition-colors z-50"
        >
          <ArrowUp size={20} />
        </button>

      </main>
    </>
  );
};

// Reusable Section Header Component
const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-4 mb-5">
    <div className="w-10 h-10 rounded-[10px] bg-[#f0f9ff] flex items-center justify-center text-[#0284c7] shrink-0 border border-[#e0f2fe]">
      {icon}
    </div>
    <h3 className="font-serif text-[22px] md:text-[24px] text-[#0f172a] font-normal tracking-tight">
      {title}
    </h3>
  </div>
);

export default CookiesPolicy;